import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Boxes, CheckCircle2 } from "lucide-react";

const LOCATIONS = ["Any", "Otorohanga", "Razza", "Te Kuiti"];
const SIZES = ["", "3x3", "3x4.5", "3x6", "3x9", "6x3", "6x6", "6x9", "Not sure"];

export default function EnquiryPublic() {
  const [form, setForm] = useState({ customer_name: "", phone: "", email: "", location: "Any", preferred_size: "", desired_date: "" });
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.customer_name.trim()) return;
    setState("submitting");
    try {
      await api.submitWebsiteEnquiry({
        customer_name: form.customer_name,
        phone: form.phone,
        email: form.email,
        location: form.location === "Any" ? "" : form.location,
        preferred_size: form.preferred_size,
        desired_date: form.desired_date,
      });
      setState("success");
    } catch (err) {
      setState("error");
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-start px-4 py-8" data-testid="enquiry-public-page">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 text-white mb-5">
          <div className="h-10 w-10 rounded-lg bg-green-600 flex items-center justify-center"><Boxes className="h-5 w-5 text-white" /></div>
          <div>
            <p className="font-display font-bold leading-tight">King Country Self Storage</p>
            <p className="text-xs text-slate-300">Storage Enquiry</p>
          </div>
        </div>

        {state === "success" ? (
          <div className="rounded-2xl bg-white p-8 text-center space-y-3" data-testid="enquiry-success">
            <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto" />
            <p className="font-display text-xl font-bold text-slate-900">Thanks — we'll be in touch!</p>
            <p className="text-sm text-slate-500">Your enquiry has been received. One of the team will contact you shortly about availability.</p>
            <Button data-testid="enquiry-another" onClick={() => { setForm({ customer_name: "", phone: "", email: "", location: "Any", preferred_size: "", desired_date: "" }); setState("idle"); }} variant="outline" className="w-full">Send another enquiry</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="rounded-2xl bg-white p-6 space-y-4 shadow-xl">
            <p className="text-sm text-slate-500">Tell us what you need and we'll find you a shed. Only your name is required.</p>
            {state === "error" && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="enquiry-error">
                Sorry, something went wrong sending your enquiry. Please try again in a moment.
              </p>
            )}
            <div className="space-y-1.5">
              <Label>Your Name *</Label>
              <Input data-testid="enquiry-name" value={form.customer_name} onChange={(e) => set("customer_name", e.target.value)} placeholder="Full name" className="text-base" required />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="enquiry-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="text-base" /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input data-testid="enquiry-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="text-base" /></div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Preferred Location</Label>
                <Select value={form.location} onValueChange={(v) => set("location", v)}>
                  <SelectTrigger data-testid="enquiry-location"><SelectValue /></SelectTrigger>
                  <SelectContent>{LOCATIONS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Preferred Size</Label>
                <Select value={form.preferred_size} onValueChange={(v) => set("preferred_size", v === "__any" ? "" : v)}>
                  <SelectTrigger data-testid="enquiry-size"><SelectValue placeholder="Any / not sure" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__any">Any / not sure</SelectItem>
                    {SIZES.filter((s) => s && s !== "Not sure").map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Desired Move-in Date</Label>
              <Input data-testid="enquiry-date" type="date" value={form.desired_date} onChange={(e) => set("desired_date", e.target.value)} className="text-base" />
            </div>
            <Button data-testid="enquiry-submit" type="submit" disabled={state === "submitting" || !form.customer_name.trim()} className="w-full h-12 text-base bg-green-600 hover:bg-green-700">
              {state === "submitting" ? "Sending..." : "Submit Enquiry"}
            </Button>
          </form>
        )}
        <p className="text-center text-[11px] text-slate-400 mt-4">King Country Self Storage · Otorohanga · Razza · Te Kuiti</p>
      </div>
    </div>
  );
}
